import { Mentions } from "antd";
import type { MentionsRef } from "antd/es/mentions";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import styles from "./PromptMentionTextArea.module.scss";
import { MENTION_TAGS, type MentionTagOption } from "./constants";

export interface PromptMentionTextAreaRef {
  focus: () => void;
  scrollIntoView: () => void;
}

interface PromptMentionTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  tags?: MentionTagOption[];
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build a regex that matches only known "@tag" tokens, e.g. /(@ma)(?![\w-])/g,
 * so "@mainstream" stays plain while "@ma" highlights.
 */
function buildHighlightRegex(tags: MentionTagOption[]): RegExp {
  const alternation = tags.map((t) => escapeRegExp(t.value)).join("|");
  return new RegExp(`(@(?:${alternation}))(?![\\w-])`, "g");
}

/**
 * Render the raw text with recognized "@tag" tokens wrapped in a highlight span.
 * This is the invisible backdrop mirror behind the visible textarea: its glyphs
 * are transparent and only the token chips paint, positioned behind the matching
 * text. Newlines are preserved via `white-space: pre-wrap`; a trailing newline
 * needs an extra node or the chip drifts one line above the textarea glyphs.
 */
function renderHighlighted(value: string, regex: RegExp): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  regex.lastIndex = 0;
  let match = regex.exec(value);
  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(<React.Fragment key={key++}>{value.slice(lastIndex, match.index)}</React.Fragment>);
    }
    nodes.push(
      <span key={key++} className={styles["mention-token"]}>
        {match[0]}
      </span>,
    );
    lastIndex = match.index + match[0].length;
    match = regex.exec(value);
  }
  if (lastIndex < value.length) {
    nodes.push(<React.Fragment key={key++}>{value.slice(lastIndex)}</React.Fragment>);
  }
  // Keep the mirror's height in sync with the textarea when text ends in "\n".
  if (value.length === 0 || value.endsWith("\n")) {
    nodes.push(<br key={key++} />);
  }
  return nodes;
}

/**
 * Prompt-instructions input with social-style "@" mention support: typing "@"
 * opens an autocomplete of known tags (antd Mentions, plain-string value) and an
 * inserted tag gets a blue highlight chip painted by an invisible backdrop mirror
 * behind the visible textarea. The stored value stays plain text ("...@ma ...").
 */
const PromptMentionTextArea = forwardRef<PromptMentionTextAreaRef, PromptMentionTextAreaProps>(
  ({ value, onChange, placeholder, height = 284, tags = MENTION_TAGS }, ref) => {
    const mentionsRef = useRef<MentionsRef>(null);
    const backdropRef = useRef<HTMLDivElement>(null);

    const highlightRegex = useMemo(() => buildHighlightRegex(tags), [tags]);

    const getTextArea = useCallback((): HTMLTextAreaElement | null => {
      const direct = mentionsRef.current?.textarea;
      if (direct) return direct;
      const el = mentionsRef.current as unknown as { nativeElement?: HTMLElement };
      return el?.nativeElement?.querySelector("textarea") ?? null;
    }, []);

    useImperativeHandle(ref, () => ({
      focus: () => mentionsRef.current?.focus(),
      scrollIntoView: () => getTextArea()?.scrollIntoView({ behavior: "smooth", block: "center" }),
    }));

    const syncScroll = useCallback(() => {
      const textarea = getTextArea();
      if (textarea && backdropRef.current) {
        backdropRef.current.scrollTop = textarea.scrollTop;
        backdropRef.current.scrollLeft = textarea.scrollLeft;
      }
    }, [getTextArea]);

    return (
      <div className={styles["mention-wrapper"]} style={{ height }}>
        <div ref={backdropRef} aria-hidden="true" className={styles["mention-backdrop"]}>
          {renderHighlighted(value, highlightRegex)}
        </div>
        <Mentions
          ref={mentionsRef}
          className={styles["mention-input"]}
          prefix="@"
          value={value}
          onChange={onChange}
          onScroll={syncScroll}
          placeholder={placeholder}
          options={tags.map((t) => ({ value: t.value, label: t.label }))}
          autoSize={false}
          notFoundContent={null}
        />
      </div>
    );
  },
);

PromptMentionTextArea.displayName = "PromptMentionTextArea";

export default PromptMentionTextArea;
